import React, { useEffect, useState } from 'react';
import useVatavData from '../../../hooks/useVatavData';

export default function VatavTransferPanel() {
  const { accounts, pledges, transfers, loading, err, msg, setErr, loadAll, submitTransfer } = useVatavData();
  const [selected, setSelected] = useState({});
  const [vatavAccountId, setVatavAccountId] = useState('');
  const [lineFields, setLineFields] = useState({});

  useEffect(() => {
    const lf = {};
    pledges.forEach((pl) => {
      const id = pl._id;
      lf[id] = {
        oldPrincipal: pl.loanDetails?.principalAmount ?? '',
        principal: '',
        percentage: '',
        articleIndices: ''
      };
    });
    setLineFields(lf);
  }, [pledges]);

  const togglePledge = (id) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const updateField = (pledgeId, key, value) => {
    setLineFields((f) => ({
      ...f,
      [pledgeId]: { ...f[pledgeId], [key]: value }
    }));
  };

  const onTransfer = async () => {
    if (!vatavAccountId) {
      setErr('Select a Vatav account.');
      return;
    }
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (ids.length === 0) {
      setErr('Select at least one pledge.');
      return;
    }
    const lines = [];
    for (const pledgeId of ids) {
      const row = lineFields[pledgeId] || {};
      let articleIndices;
      if (String(row.articleIndices || '').trim()) {
        articleIndices = String(row.articleIndices)
          .split(/[,;\s]+/)
          .map((x) => parseInt(x, 10))
          .filter((n) => Number.isInteger(n));
        if (articleIndices.length === 0) articleIndices = undefined;
      }
      lines.push({
        pledgeId,
        oldPrincipal: row.oldPrincipal,
        principal: row.principal,
        percentage: row.percentage,
        articleIndices
      });
    }
    try {
      await submitTransfer({ vatavAccountId, lines });
      setSelected({});
    } catch {
      /* err in hook */
    }
  };

  return (
    <div style={{ padding: '0 8px' }}>
      <p style={{ fontSize: 14, color: '#444', marginTop: 0 }}>
        Select pledges, enter old principal, Vatav principal, and percentage. Optional article indices (e.g.{' '}
        <code>0,2</code>) split items to Vatav.
      </p>
      {err && <p style={{ color: 'crimson', marginBottom: 8 }}>{err}</p>}
      {msg && <p style={{ color: 'green', marginBottom: 8 }}>{msg}</p>}

      <div style={{ marginBottom: 8 }}>
        <label>
          Vatav account:{' '}
          <select value={vatavAccountId} onChange={(e) => setVatavAccountId(e.target.value)}>
            <option value="">— select —</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name} {a.mobileNo ? `(${a.mobileNo})` : ''}
              </option>
            ))}
          </select>
        </label>
        <button type="button" style={{ marginLeft: 12 }} onClick={onTransfer} disabled={loading}>
          Transfer selected
        </button>
        <button type="button" style={{ marginLeft: 8 }} onClick={loadAll} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <table border="1" cellPadding={6} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead style={{ background: '#eee' }}>
              <tr>
                <th />
                <th>Ticket</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Old principal</th>
                <th>Principal (Vatav)</th>
                <th>%</th>
                <th>Article idx</th>
              </tr>
            </thead>
            <tbody>
              {pledges.map((pl) => {
                const id = pl._id;
                const lf = lineFields[id] || {};
                const cust = [pl.customer?.firstName, pl.customer?.lastName].filter(Boolean).join(' ');
                return (
                  <tr key={id}>
                    <td>
                      <input type="checkbox" checked={!!selected[id]} onChange={() => togglePledge(id)} />
                    </td>
                    <td>{pl.loanDetails?.ticketNo}</td>
                    <td>{cust}</td>
                    <td>{(pl.articles || []).length}</td>
                    <td>
                      <input
                        style={{ width: 90 }}
                        value={lf.oldPrincipal}
                        onChange={(e) => updateField(id, 'oldPrincipal', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 90 }}
                        value={lf.principal}
                        onChange={(e) => updateField(id, 'principal', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 60 }}
                        value={lf.percentage}
                        onChange={(e) => updateField(id, 'percentage', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 100 }}
                        value={lf.articleIndices}
                        onChange={(e) => updateField(id, 'articleIndices', e.target.value)}
                        placeholder="0,1"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3>Recent transfers</h3>
      <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
        <table border="1" cellPadding={5} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, background: '#fff' }}>
          <thead style={{ background: '#eee' }}>
            <tr>
              <th>Vatav ticket</th>
              <th>Account</th>
              <th>Pledge ticket</th>
              <th>Customer</th>
              <th>Old pr.</th>
              <th>Pr.</th>
              <th>%</th>
              <th>Split</th>
            </tr>
          </thead>
          <tbody>
            {transfers.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>
                  No transfers yet.
                </td>
              </tr>
            )}
            {transfers.map((tr) => (
              <tr key={tr._id}>
                <td>{tr.vatavTicketNo}</td>
                <td>{tr.vatavAccountName}</td>
                <td>{tr.pledgeTicketNo}</td>
                <td>{tr.customerName}</td>
                <td>{tr.oldPrincipal}</td>
                <td>{tr.principal}</td>
                <td>{tr.percentage}</td>
                <td>{tr.split ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
